<template>
  <q-form :data-testid="definition.formTestId" @submit.prevent="$emit('submit')">
    <form-section title="Параметры операции" :description="sectionDescription" :disabled="pending">
      <form-grid>
        <q-input :model-value="form.date" outlined dense type="date" label="Дата" :data-testid="testId('date')" @update:model-value="set('date', $event)" />

        <template v-if="kind === 'transfer'">
          <q-select :model-value="form.source_warehouse_id" outlined dense emit-value map-options label="Со склада" :options="warehouseOptions" data-testid="warehouse-transfer-source" @update:model-value="set('source_warehouse_id', $event)" />
          <q-select :model-value="form.destination_warehouse_id" outlined dense emit-value map-options label="На склад" :options="warehouseOptions" data-testid="warehouse-transfer-destination" :error="sameWarehouse" :error-message="sameWarehouse ? 'Склад назначения должен отличаться от склада-источника' : ''" @update:model-value="set('destination_warehouse_id', $event)" />
        </template>
        <q-select v-else :model-value="form.warehouse_id" outlined dense emit-value map-options label="Склад" :options="warehouseOptions" :data-testid="testId('warehouse')" @update:model-value="set('warehouse_id', $event)" />

        <q-select class="form-grid__full" :model-value="form.nomenclature_id" outlined dense emit-value map-options label="Номенклатура" :options="nomenclatureOptions" :data-testid="testId('nomenclature')" @update:model-value="set('nomenclature_id', $event)" />

        <template v-if="kind === 'opening'">
          <q-input :model-value="form.count" outlined dense type="number" step="0.0001" min="0" label="Количество" data-testid="warehouse-opening-count" @update:model-value="set('count', $event)" />
          <q-input :model-value="moneyInput(form.amount, 'amount')" outlined dense type="number" step="0.01" min="0" label="Сумма" data-testid="warehouse-opening-amount" @update:model-value="setMoney('amount', $event)" @blur="finishMoney('amount')" />
          <q-select :model-value="form.debit_account_id" outlined dense emit-value map-options label="Счет учета" :options="inventoryAccountOptions" data-testid="warehouse-opening-debit-account" @update:model-value="set('debit_account_id', $event)" />
          <q-select :model-value="form.credit_account_id" outlined dense emit-value map-options clearable label="Корреспондирующий счет" :options="accountOptions" data-testid="warehouse-opening-credit-account" @update:model-value="set('credit_account_id', $event)" />
          <q-input class="form-grid__full" :model-value="form.comment" outlined dense autogrow label="Комментарий" data-testid="warehouse-opening-comment" @update:model-value="set('comment', $event)" />
        </template>

        <template v-else-if="kind === 'transfer'">
          <q-input :model-value="form.count" outlined dense type="number" step="0.0001" min="0" label="Количество" data-testid="warehouse-transfer-count" @update:model-value="set('count', $event)" />
          <q-select :model-value="form.inventory_account_id" outlined dense emit-value map-options label="Счет учета" :options="inventoryAccountOptions" data-testid="warehouse-transfer-account" @update:model-value="set('inventory_account_id', $event)" />
          <q-input class="form-grid__full" :model-value="form.comment" outlined dense autogrow label="Комментарий" data-testid="warehouse-transfer-comment" @update:model-value="set('comment', $event)" />
        </template>

        <template v-else-if="kind === 'adjustment'">
          <q-input :model-value="form.target_quantity" outlined dense type="number" step="0.0001" min="0" label="Фактическое количество" data-testid="warehouse-adjustment-target" @update:model-value="set('target_quantity', $event)" />
          <q-input :model-value="moneyInput(form.price, 'price')" outlined dense type="number" step="0.01" min="0" label="Цена излишка" data-testid="warehouse-adjustment-price" @update:model-value="setMoney('price', $event)" @blur="finishMoney('price')" />
          <q-select :model-value="form.inventory_account_id" outlined dense emit-value map-options label="Счет учета" :options="inventoryAccountOptions" data-testid="warehouse-adjustment-account" @update:model-value="set('inventory_account_id', $event)" />
          <q-input class="form-grid__full" :model-value="form.reason" outlined dense autogrow label="Причина" data-testid="warehouse-adjustment-reason" @update:model-value="set('reason', $event)" />
        </template>

        <template v-else>
          <q-input :model-value="form.count" outlined dense type="number" step="0.0001" min="0" label="Количество" data-testid="warehouse-writeoff-count" @update:model-value="set('count', $event)" />
          <q-select :model-value="form.inventory_account_id" outlined dense emit-value map-options label="Счет учета" :options="inventoryAccountOptions" data-testid="warehouse-writeoff-account" @update:model-value="set('inventory_account_id', $event)" />
          <q-select :model-value="form.expense_account_id" outlined dense emit-value map-options clearable label="Счет затрат" :options="accountOptions" data-testid="warehouse-writeoff-expense-account" @update:model-value="set('expense_account_id', $event)" />
          <q-input class="form-grid__full" :model-value="form.reason" outlined dense autogrow label="Причина" data-testid="warehouse-writeoff-reason" @update:model-value="set('reason', $event)" />
        </template>
      </form-grid>
    </form-section>

    <slot />
  </q-form>
</template>

<script>
import { formatMoney } from '../../../services/moneyFormat'
import { FormGrid, FormSection } from '../../../components/ui'

export default {
  name: 'WarehouseOperationForm',
  components: { FormGrid, FormSection },
  emits: ['change', 'submit', 'update:form'],
  props: {
    kind: { type: String, required: true },
    definition: { type: Object, required: true },
    form: { type: Object, required: true },
    pending: { type: Boolean, default: false },
    warehouseOptions: { type: Array, default: () => [] },
    nomenclatureOptions: { type: Array, default: () => [] },
    accountOptions: { type: Array, default: () => [] },
    inventoryAccountOptions: { type: Array, default: () => [] }
  },
  data() { return { editingMoney: '' } },
  computed: {
    sameWarehouse() {
      return this.kind === 'transfer' && this.form.source_warehouse_id && this.form.destination_warehouse_id && String(this.form.source_warehouse_id) === String(this.form.destination_warehouse_id)
    },
    sectionDescription() {
      if (this.kind === 'adjustment') return 'Фактическое количество сравнивается с системным остатком. Итоговое расхождение появится после проведения.'
      if (this.kind === 'writeOff') return 'Доступный остаток проверяется при проведении.'
      return 'Обязательные реквизиты используются для складского движения и бухгалтерской проводки.'
    }
  },
  methods: {
    moneyInput(value, field) {
      if (this.editingMoney === field) return value
      return formatMoney(value, { empty: '', invalid: String(value), useGrouping: false }).replace(',', '.')
    },
    setMoney(field, value) {
      this.editingMoney = field
      this.set(field, value)
    },
    finishMoney(field) {
      if (this.editingMoney === field) this.editingMoney = ''
    },
    set(field, value) {
      this.$emit('update:form', { ...this.form, [field]: value })
      this.$emit('change')
    },
    testId(field) {
      const prefix = this.kind === 'writeOff' ? 'writeoff' : this.kind
      return `warehouse-${prefix}-${field}`
    }
  }
}
</script>

<style scoped>
.form-grid__full { --form-grid-span: 12; --form-grid-tablet-span: 2; }
</style>
